import Breadcrumb from 'Components/UI/Breadcrumb';
import React, { useState, useEffect } from 'react'
import { Button, Card, CardBody, CardTitle, Col, Container, Form as ReactstrapForm, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import Editor from 'Components/Form/Editor/Editor';
import Select from "react-select";
import Dropzone from "Components/Form/DropZone";
import placeholderImage from "assets/images/img.png";
import { getAllCategories } from 'helpers/API Calls/Operations/category';
import { getProducts } from 'helpers/API Calls/Operations/Products';
import { useNavigate } from 'react-router-dom';
import Spinners from "Components/UI/Spinner";
import { createPackage, getPackageGroups } from 'helpers/API Calls/Operations/packages';
import Hint from 'Components/UI/Hint';

interface PackageFormValues {
  package_name: string;
  main_category: any;
  price: number;
  is_featured: boolean;
  is_public: boolean;
  is_seo: boolean;
  categories: any[];
  products: any[];
  groups: any[];
  description: string;
  meta_title: string;
  meta_description: string;
}

function CreatePackage() {
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string>('');
  const [dropdownOptions, setDropdownOptions] = useState<{
    categoryOptions: any[];
    productOptions: any[];
    groupOptions: any[];
  }>({
    categoryOptions: [],
    productOptions: [],
    groupOptions: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const formik = useFormik<PackageFormValues>({
    initialValues: {
      package_name: '',
      main_category: null,
      price: 0,
      is_featured: false,
      is_public: false,
      is_seo: false,
      categories: [],
      products: [],
      groups: [],
      description: '',
      meta_title: '',
      meta_description: '',
    },
    validationSchema: yup.object().shape({
      package_name: yup.string().required('Package name is required'),
      main_category: yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      }).nullable().required('Please select main category'),
      price: yup.number().min(0, 'Price must be positive').required('Price is required'),
      categories: yup.array().of(yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })),
      products: yup.array().of(yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })),
      groups: yup.array().of(yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      })),
      meta_title: yup.string().when('is_seo', {
        is: true, 
        then: schema => schema.required('Meta title is required when SEO is enabled')
      }),
      meta_description: yup.string().when('is_seo', {
        is: true, 
        then: schema => schema.required('Meta description is required when SEO is enabled')
      }),
    }),
    onSubmit: async (values: PackageFormValues, { setSubmitting }) => {
      setSubmitting(true);

      if (!selectedImage) {
        setImageError('Featured image is required');
        setSubmitting(false);
        const imageElement = document.getElementById('featured-image-section');
        if (imageElement) {
          imageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      const packageData = {
        package_name: values.package_name,
        is_public: values.is_public ? 1 : 0,
        is_featured: values.is_featured ? 1 : 0,
        is_seo: values.is_seo ? 1 : 0,
        main_category_id: values.main_category?.value,
        price: values.price,
        products: values.products.map(product => product.value),
        categories: values.categories.map(category => category.value),
        groups: values.groups.map(group => group.value),
        description: description,
        meta_title: values.meta_title,
        meta_description: values.meta_description
      };
      const response = await createPackage(packageData, selectedImage);
      setSubmitting(false);
      if(response.success){
        formik.resetForm();
        setSelectedImage(null);
        setDescription('');
        navigate('/packages');
      }
    },
  });

  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);

      let categoryOptions = [];
      let productOptions = [];
      let groupOptions = [];

      const categories = await getAllCategories();
      if(categories.success) {
        categoryOptions = categories.data?.items?.map((category: any) => ({
          label: category.category_name, 
          value: category.id
        }));
      }

      const products = await getProducts('limit=1000&is_addon=0');
      if(products.success) {
        productOptions = products.data?.items?.map((product: any) => ({
          label: product.product_name, 
          value: product.id
        }));
      }

      const groups = await getPackageGroups('limit=1000');
      if(groups.success) {
        groupOptions = groups.data?.items?.map((group: any) => ({
          label: group.group_name, 
          value: group.id
        }));
      }

      setDropdownOptions({
        categoryOptions,
        productOptions,
        groupOptions
      });

      setIsLoading(false);
    };
    getData();
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Packages", link: "/packages" }, {label: 'Create'}]} />
        {isLoading ? <div className="position-relative vh-100"><Spinners/></div>
        :
        <Card className="shadow-sm">
          <CardBody className="p-4">
            <ReactstrapForm onSubmit={(e) => {
              e.preventDefault();
              formik.handleSubmit(e);
            }} autoComplete="off">
              
              {/* Basic Details Section */}
              <div className="pb-3 mb-4">
                <CardTitle tag="h4" className="mb-4 ">Basic Details</CardTitle>
                <Row >
                  <Col xl="6">
                    <div className="mb-3">
                      <Label htmlFor="package_name" className="form-label ">Package Name <span className="text-danger">*</span></Label>
                      <Input
                        id="package_name"
                        name="package_name"
                        type="text"
                        placeholder="Enter package name"
                        value={formik.values.package_name}
                        onChange={formik.handleChange}
                        invalid={formik.touched.package_name && formik.errors.package_name ? true : false}
                      />
                      {formik.errors.package_name && formik.touched.package_name ? (
                        <FormFeedback type="invalid">{formik.errors.package_name}</FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-0">
                      <Label htmlFor="price" className="form-label ">Price <span className="text-danger">*</span></Label>
                      <Input
                        id="price"
                        name="price"
                        type="number"
                        placeholder="Enter price"
                        value={formik.values.price}
                        onChange={formik.handleChange}
                        invalid={formik.touched.price && formik.errors.price ? true : false}
                      />
                      {formik.errors.price && formik.touched.price && (
                        <FormFeedback type="invalid">{formik.errors.price}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3" id="featured-image-section">
                      <Label htmlFor="name-input">Featured Image <span className="text-danger">*</span></Label>
                      <Dropzone
                        accept = {{"image/*": [".png", ".jpeg", ".jpg"]}}
                        onDrop = {(acceptedFiles) => {
                          setSelectedImage(acceptedFiles[0]);
                          setImageError('');
                        }}
                        // onDelete={() => {setSelectedImage(null);}}
                        // showDelete={!!selectedImage}
                        renderContent = {() => (
                          <div className="text-center cursor-pointer">
                            <div className="position-relative d-inline-block" >
                              <div className={`avatar-xl rounded`}>
                                <div className="avatar-title bg-light ">
                                  <img
                                    src={selectedImage ? URL.createObjectURL(selectedImage) : placeholderImage}
                                    id="logo-img"
                                    alt=""
                                    className="avatar-xl rounded"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      />
                      {imageError && (
                        <div className="text-danger mt-1 small">{imageError}</div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Categories Section */}
              <div className="pb-3 mb-4">
                <CardTitle tag="h4" className="mb-4 ">Categories</CardTitle>
                <Row >
                  <Col xl="6">
                    <div className="mb-0">
                      <Label htmlFor="main_category" className="form-label">Main Category <span className="text-danger">*</span></Label>
                      <Select
                        id="main_category"
                        name="main_category"
                        placeholder="Select main category"
                        options={dropdownOptions.categoryOptions}
                        value={formik.values.main_category}
                        onChange={(value) => {
                          formik.setFieldValue("main_category", value);
                          if (value) {
                            const existingCategories = formik.values.categories || [];
                            if (!existingCategories.some((cat) => cat.value === value.value)) {
                              formik.setFieldValue("categories", [...existingCategories, value]);
                            }
                          }
                        }}
                        className={formik.touched.main_category && formik.errors.main_category ? "is-invalid" : ""}
                        classNamePrefix="select"
                      />
                      {formik.touched.main_category && formik.errors.main_category && (
                        <div className="invalid-feedback d-block">
                          {typeof formik.errors.main_category === 'string' && formik.errors.main_category}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col xl="6">
                    <div className="mb-0">
                      <Label htmlFor="categories" className="form-label ">Additional Categories</Label>
                      <Select
                        id="categories"
                        name="categories"
                        placeholder="Select categories"
                        options={dropdownOptions.categoryOptions}
                        value={formik.values.categories}
                        onChange={(value) => formik.setFieldValue("categories", value)}
                        isMulti
                        classNamePrefix="select"
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Products and Groups Section */}
              <div className="pb-3 mb-4">
                <CardTitle tag="h4" className="mb-4 ">Products and Groups</CardTitle>
                <Row >
                  <Col sm="6">
                    <div className="mb-0">
                      <Label htmlFor="products" className="form-label ">Products</Label>
                      <Select
                        id="products"
                        name="products"
                        placeholder="Select products"
                        options={dropdownOptions.productOptions}
                        value={formik.values.products}
                        onChange={(value) => formik.setFieldValue("products", value)}
                        isMulti
                        classNamePrefix="select"
                      />
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-0">
                      <Label htmlFor="groups" className="form-label ">Groups</Label>
                      <Select
                        id="groups"
                        name="groups"
                        placeholder="Select groups"
                        options={dropdownOptions.groupOptions}
                        value={formik.values.groups}
                        onChange={(value) => formik.setFieldValue("groups", value)}
                        isMulti
                        classNamePrefix="select"
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Settings Section */}
              <div className="pb-3 mb-4">
                <CardTitle tag="h4" className="mb-4 ">Settings</CardTitle>
                <div className="d-flex gap-4">
                  <div className="form-check mb-0">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="is_featured"
                      name="is_featured"
                      checked={formik.values.is_featured}
                      onChange={formik.handleChange}
                    />
                    <Label className="form-check-label fw-medium" htmlFor="is_featured">
                      Featured Package
                    </Label>
                  </div>
                  <div className="form-check mb-0">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="is_public"
                      name="is_public"
                      checked={formik.values.is_public}
                      onChange={formik.handleChange}
                    />
                    <Label className="form-check-label fw-medium" htmlFor="is_public">
                      Public Package
                    </Label>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="pb-3 mb-4">
                <CardTitle tag="h4" className="mb-4 ">Description</CardTitle>
                <Editor content={description} setContent={setDescription} />
              </div>

              {/* SEO Information Section */}
              <div className="mb-4">
                <CardTitle tag="h4" className="mb-4 ">SEO Information</CardTitle>

                <div className="mb-3">
                  <div className="form-check mb-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="is_seo"
                      name="is_seo"
                      checked={formik.values.is_seo}
                      onChange={formik.handleChange}
                    />
                    <Label className="form-check-label" htmlFor="is_seo">
                      Index this package in search engines <Hint type="index" />
                    </Label>
                  </div>
                </div>

                <Row >
                  <Col xl="6">
                    <div className="mb-0">
                      <Label htmlFor="meta_title" className="form-label ">Meta Title {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="title" /></Label>
                      <Input
                        id="meta_title"
                        name="meta_title"
                        type="text"
                        placeholder="Enter meta title"
                        value={formik.values.meta_title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.meta_title && formik.errors.meta_title ? true : false}
                      />
                      {formik.errors.meta_title && formik.touched.meta_title ? (
                        <FormFeedback type="invalid">{formik.errors.meta_title}</FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col xl="6">
                    <div className="mb-0">
                      <Label htmlFor="meta_description" className="form-label ">Meta Description {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="description" /></Label>
                      <Input
                        tag="textarea"
                        rows="3"
                        id="meta_description"
                        name="meta_description"
                        placeholder="Enter meta description"
                        value={formik.values.meta_description}
                        onChange={formik.handleChange}
                        invalid={formik.touched.meta_description && formik.errors.meta_description ? true : false}
                      />
                      {formik.errors.meta_description && formik.touched.meta_description ? (
                        <FormFeedback type="invalid">{formik.errors.meta_description}</FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="d-flex flex-wrap gap-2 justify-content-end">
                <Button 
                  type="submit" 
                  color="primary" 
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? 'Creating...' : 'Create Package'}
                </Button>
              </div>
            </ReactstrapForm>
          </CardBody>
        </Card>}
      </Container>
    </div>
  );
}

export default CreatePackage;
